export type FilterConstantsResponse = {
  brands: Record<string, string>;
  categories: Record<string, string>;
  savedViews: Record<string, string>;
  countries: Record<string, string>;
  dateRanges: string[];
}

export type BrandsAndCategoriesResponse = {
  brands: BrandSearchResult[];
  categories: CategorySearchResult[];
}

export type BrandSearchResult = {
  brandKey: string;
  name: string;
  logoPath: string;
  type: string;
  categoryName: string;
}

export type CategorySearchResult = {
  id: string;
  name: string;
}

export type AdvertiserDomainsResponse = {
  domains: string[];
}

export type TopLevelMetricsResponse = {
  items: { name: string, value: number }[]
}

export interface CommonDashboardFilterResponse {
  countries: CountriesFilterData;
}

export interface CountriesFilterData {
  [countryCode: string]: string;
}

export enum DeviceFilterType {
  ALL_DEVICES = 'All Devices',
  DESKTOP = 'Desktop',
  MOBILE = 'Mobile & Tablet',
}

export enum PosthogEventName {
  INVENTORY_QUALITY_TABLE_PAGINATION = "table_navigation",
  FILTERED_CONTENT_CATEGORY = "user_interest_selected",
  METRIC_SELECTED = "metric_selected",
  FILTERED_BRANDS = "global_filter_brand _selected",
  SELECTED_DATE_RANGE = "global_filter_date_range_selected",
  FILTERED_DEVICE = "global_filter_device_selected",
  FILTERED_COUNTRY = "global_filter_country_selected",
  FILTERED_PLATFORM = "global_filter_platform_selected",
  MAP_REGION_SELECTED = "map_region_selected",
  CREATIVE_CARD_VIEW = "creative_card_view",
  CREATIVE_CARD_CLOSED = "creative_card_closed",
  TABLE_EXPORTED = "table_exported",
  POLARIS_PROMPT_SENT = "polaris_prompt_sent",
  POLARIS_MESSAGE_ACTION = "polaris_message_action",
  POLARIS_TOOL_SELECTED = "polaris_tool_selected",
  EXTERNAL_LINK_CLICKED = "external_link_clicked",
  SIGN_UP_SUCCEEDED = "sign_up_succeeded",
  SIGN_UP_FAILED = "sign_up_failed",
  BRAND_SEARCHED = "brand_searched",
  BRAND_SELECTED = "brand_selected",
  PROFILE_SUBMITTED = "profile_submitted",
  LOG_IN_LINK_CLICKED = "log_in_link_clicked",
  CONTINUE_WITH_GOOGLE_BUTTON_CLICKED = "continue_with_google_button_clicked",
  WIDGET_VOLUME_FILTER_APPLIED = "widget_volume_filter_applied",
  CHAT_TITLE_EDITED = "chat_title_edited",
  POLARIS_DECK_WIZARD_REQUESTED = "polaris_deck_wizard_requested",
  POLARIS_DECK_WIZARD_GENERATED = "polaris_deck_wizard_generated",
  GROUP_BY_SELECTED = "group_by_selected"
}

export enum PosthogEventCode {
  INVENTORY_QUALITY_TABLE_PAGINATION = "table_navigation",
  FILTERED_CONTENT_CATEGORY = "user_interest_selected",
  SELECTED_DATE_RANGE = "selected_date",
  RELATIVE_DATE = "relative_date",
  WIDGET_NAME = 'widget_name',
  METRIC_NAME = "selected_metric",
  MY_BRAND = "my_brand_key",
  MY_COMPETITORS = "my_competitors_keys",
  FILTERED_DEVICE = "selected_device",
  FILTERED_COUNTRY = "selected_country",
  FILTERED_PLATFORM = "selected_platform",
  MAR_REGION = "selected_region",
  CREATIVE_CARD_VIEW = "creative_id",
  ACTION_TYPE = "action_type",
  REPORT_TYPE = "report_type",
  ORIGIN = "origin",
  PROMPT_CONTENT = "prompt_content",
  TOOL_NAME = "tool_name",
  CLICK_URL = "click_url",
  CLICK_TEXT = "click_text",
  VOLUME_THRESHOLD = "volume_threshold",
  GROUP_BY_OPTION = "group_by_option"
}

export enum MyBrandWidgetApiPaths {
  INVENTORY_QUALITY = '/dashboard/my-brand/inventory-quality',
  SITES_PERFORMANCE = '/dashboard/my-brand/sites-performance',
  PERFORMANCE_MAP_WORLDWIDE = '/dashboard/my-brand/performance-map/worldwide',
  PERFORMANCE_MAP_USA = '/dashboard/my-brand/performance-map/usa',
  CONTENT_CATEGORY_BREAKDOWN = '/dashboard/my-brand/content-category-breakdown',
  CREATIVE_GALLERY = '/dashboard/my-brand/creative-gallery',
  CREATIVE_DATA = '/dashboard/my-brand/creative-data',
  OVERVIEW = '/dashboard/my-brand/overview',
  PERFORMANCE_OVER_TIME = '/dashboard/my-brand/performance-over-time',
  AUDIENCE_GENDER = '/dashboard/my-brand/audience-gender'
}

export enum MyCompetitionWidgetApiPaths {
  FILTERS = '/dashboard/my-competition/filters',
  INVENTORY_QUALITY = '/dashboard/my-competition/inventory-quality',
  BRANDS_IMPRESSIONS_DISTRIBUTION = '/dashboard/my-competition/brands-impressions-distribution',
  PERFORMANCE_OVER_TIME = '/dashboard/my-competition/performance-over-time',
  PERFORMANCE_COMPARISON = '/dashboard/my-competition/performance-comparison',
  CREATIVE_GALLERY = '/dashboard/my-competition/creative-gallery',
  CREATIVE_DATA = '/dashboard/my-competition/creative-data',
  PERFORMANCE_MAP_WORLDWIDE = '/dashboard/my-competition/performance-map/worldwide',
  PERFORMANCE_MAP_USA = '/dashboard/my-competition/performance-map/usa',
  WORD_CLOUD = '/dashboard/my-competition/word-cloud',
  OVERVIEW = '/dashboard/my-competition/overview',
  CONTENT_CATEGORY_DISTRIBUTION = '/dashboard/my-competition/content-category-distribution',
  PERFORMANCE_BY_TIME = '/dashboard/my-competition/performance-by-time',
  PERFORMANCE_BY_DEVICE = '/dashboard/my-competition/performance-by-device',
  SPEND_EFFICIENCY = '/dashboard/my-competition/spend-efficiency',
  AUDIENCE_GENDER = '/dashboard/my-competition/audience-gender',
  STRATEGY_DECK = '/dashboard/my-competition/strategy-deck'
}

export enum MyCampaignsWidgetApiPaths {
  OVERVIEW = '/dashboard/my-campaigns/overview',
  CONTENT_CATEGORY_BREAKDOWN = '/dashboard/my-campaigns/content-category-breakdown',
  SITES_PERFORMANCE = '/dashboard/my-campaigns/sites-performance',
  REFRESH_RATE_DISTRIBUTION = '/dashboard/my-campaigns/refresh-rate-distribution',
  INVENTORY_QUALITY = '/dashboard/my-campaigns/inventory-quality',
}

export enum SocialOverviewWidgetApiPaths {
  OVERVIEW_DASHBOARD = '/dashboard/social-overview/overview-dashboard',
  PERFORMANCE_OVER_TIME = '/dashboard/social-overview/performance-over-time',
  CREATIVE_GALLERY = '/dashboard/social-overview/creative-gallery',
  CREATIVE_DATA = '/dashboard/social-overview/creative-data'
}

export enum FreemiumApiPaths {
  BRANDS_SEARCH = '/brands/search',
  TRENDING_BRANDS = '/brands/trending',
  TOP_COMPETITORS = '/brands/top-competitors',
  SIGNUP = '/users/signup',
  OPTIONS = '/users/signup/options',
  UPDATE_BRANDS = '/users/signup/update/brands',
  UPDATE_PROFESSIONAL_DETAILS = '/users/signup/update/professional-details',
  COMPLETE = '/users/signup/complete',
  RESET_EMAIL = '/users/send-password-reset-email'
}
